import React, { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import RecipeBlogPosts from "../RecipeBlogPosts";

import { GetWine_wine } from "../../../types/__generated__/GetWine";
import { I18N_NAMESPACES } from "../../../helpers/constants";

import WineCharacteristicsSection from "../../atoms/WineCharacteristicsSection";
import { PairingGuideWrapper, PairingGuideTitle, PairingDescription, TextWrapper, SeeMore } from "./style";

interface PairingGuideProps {
  wine: GetWine_wine | null | undefined;
  isWineDetailsPage: boolean;
}

const PairingGuide = ({ wine, isWineDetailsPage }: PairingGuideProps) => {
  const [isReadMoreShown, setIsReadMoreShown] = useState<boolean>(true);
  const buttonLocaleKey = isReadMoreShown ? "readMore" : "readLess";

  const { t } = useTranslation([
    I18N_NAMESPACES.MY_BADGES,
    I18N_NAMESPACES.WINE_PAGES,
    I18N_NAMESPACES.COMMON,
  ]);

  const isPairingDescriptionLong = (wine?.pairingsDescription?.length ?? 251) > 250;

  const renderPairingDescription = (): ReactElement => (
    <>
      <TextWrapper isOpen={!isReadMoreShown} isPairingDescriptionLong={isPairingDescriptionLong}>
        <PairingDescription>{wine?.pairingsDescription}</PairingDescription>
      </TextWrapper>
      {isPairingDescriptionLong && (
        <SeeMore onClick={() => setIsReadMoreShown(!isReadMoreShown)}>
          {t(`common:${buttonLocaleKey}`)}
        </SeeMore>
      )}
    </>
  );

  const renderFoodSection = (): ReactElement => (
    <WineCharacteristicsSection title={t("winePages:food")} characteristics={wine?.food} />
  );

  const renderTastesSection = (): ReactElement => (
    <WineCharacteristicsSection title={t("winePages:tastes")} characteristics={wine?.tastes} />
  );

  const renderMoodsSection = (): ReactElement => (
    <WineCharacteristicsSection title={t("winePages:moods")} characteristics={wine?.moods} />
  );

  const renderSeasonsSection = (): ReactElement => (
    <WineCharacteristicsSection
      title={t("winePages:seasons")}
      characteristics={wine?.seasons}
      isSeasonsSection
    />
  );

  const amountRecipes = wine?.recipes?.length ?? 0;
  const renderRecipes = (): ReactElement | null =>
    amountRecipes > 0 ? (
      <>
        <PairingGuideTitle isWineDetailsPage={isWineDetailsPage}>
          {t("winePages:recipeMatches")}
        </PairingGuideTitle>
        <RecipeBlogPosts recipes={wine?.recipes} isWineDetailsPage={isWineDetailsPage} />
      </>
    ) : null;

  const renderContent = (): ReactElement => (
    <PairingGuideWrapper isWineDetailsPage={isWineDetailsPage}>
      <PairingGuideTitle isWineDetailsPage={isWineDetailsPage}>
        {t("winePages:pairingGuide")}
      </PairingGuideTitle>
      {!!wine?.pairingsDescription && renderPairingDescription()}
      {renderFoodSection()}
      {renderTastesSection()}
      {renderMoodsSection()}
      {renderSeasonsSection()}
      {renderRecipes()}
    </PairingGuideWrapper>
  );

  return renderContent();
};

export default PairingGuide;
